import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';

import createFuzzySearch from '@nozbe/microfuzz';
import { ClientIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { CloseIcon, SearchIconSmall, SpinnerDarkIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useDebounce from 'hooks/useDebounce';
import { createClient } from 'services/profile-editor';
import { companyNames, matchesCompanyName } from 'utils/company-options';
import { ThemeProvider } from 'styled-components';

import {
  ClientSelectOptionContainer,
  ClientSelectOptionLabel,
  ClientSelectOptionsContainer,
  ClientSelectSelectedClientsContainer,
  ClientSelectSelectedClientContainer,
  ClientSelectSelectedClientLabel,
  ClientSelectWrapper,
  ClientSelectSelectedClientRemoveContainer,
  ClientSelectInput,
  ClientSelectOptionAdd,
  ClientSelectIconContainer,
  ClientSelectOptionAlreadyAdded,
} from './styled';
import useClients from './useClients';

interface ClientSelectProps {
  name?: string;

  placeholder?: string;

  onChange?: (values: any[]) => void;

  allowMultiple?: boolean;

  allowCreate?: boolean;

  preselectedValues?: any[];
}

export default function ClientSelect({
  placeholder = 'No client selected',
  name,
  onChange,
  allowMultiple = false,
  allowCreate = false,
  preselectedValues = [],
}: ClientSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const { data } = useClients();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedClients, setSelectedClients] = useState<any[]>(preselectedValues);
  const [currentInputValue, setCurrentInputValue] = useState<string>('');
  const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false);
  const [creationError, setCreationError] = useState<string>('');
  const [list, setList] = useState<any[]>([]);
  const debouncedValue = useDebounce(currentInputValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && selectedClients.length > 0 && currentInputValue.trim() === '') {
      const lastClient = selectedClients[selectedClients.length - 1];
      handleClientClick(lastClient);
    }
  };

  const clients = useMemo(() => {
    return data?.all || [];
  }, [data]);

  const handleCreateClient = async (params) => {
    const response = await createClient(params);
    const client = response.data as any;
    if (!response.ok || !client?.uuid || !client?.name) {
      throw new Error(client?.message || 'Could not add this client. Please try again.');
    }
    return client;
  };

  const { mutate } = useMutation(handleCreateClient, {
    onMutate: () => {
      setCreationError('');
      setCurrentlySubmitting(true);
    },
    onSuccess: (client) => {
      if (!selectedClients.some((selected) => selected.uuid === client.uuid)) {
        handleClientClick(client);
      }
      setCurrentInputValue('');
      setIsOpen(false);
    },
    onError: (error: Error) => setCreationError(error.message),
    onSettled: () => setCurrentlySubmitting(false),
  });

  const handleClientClick = (client) => {
    setCreationError('');
    let finalClients = [];

    const clientIndex = selectedClients.findIndex(
      (selectedClient) => selectedClient.uuid === client.uuid
    );

    finalClients =
      clientIndex === -1
        ? allowMultiple
          ? [...selectedClients, client]
          : [client]
        : selectedClients.filter((selectedClient) => selectedClient.uuid !== client.uuid);

    setSelectedClients(finalClients);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalClients.map((client) => client.uuid)
      );
    }

    setCurrentInputValue('');
    onChange?.(finalClients);
    setIsOpen(false);
  };

  useOnClickOutside(wrapperRef, () => {
    if (isOpen) {
      formikContext.setFieldTouched(name, true, false);
    }
    setIsOpen(false);
  });

  const fuzzySearch = useMemo(() => {
    if (clients) {
      return createFuzzySearch(
        clients.flatMap((client) => companyNames(client).map((name) => ({ name, client }))),
        { key: 'name' }
      );
    }
  }, [clients]);

  const fuzzySearchCallback = useCallback(
    (value) => {
      if (fuzzySearch) {
        const matches = fuzzySearch(value.trim()).map((result) => result.item.client);
        return Array.from(new Map(matches.map((client) => [client.uuid, client])).values());
      }
    },
    [debouncedValue, fuzzySearch]
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInputValue(e.target.value);
  };

  const handleWrapperClick = () => {
    if (isOpen) return;

    inputRef.current.focus();
    setIsOpen(true);
  };

  useEffect(() => {
    if (fuzzySearch && debouncedValue.trim() !== '') {
      setList(fuzzySearchCallback(debouncedValue));
    } else {
      setList(clients);
    }
  }, [debouncedValue, fuzzySearchCallback, clients]);

  const handleClientRemoveClick = (
    client: any,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    const finalClients = selectedClients.filter(
      (selectedCompany) => selectedCompany.uuid !== client.uuid
    );

    setSelectedClients(finalClients);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalClients.map((client) => client.uuid)
      );
    }

    if (isOpen) {
      setIsOpen(false);
    }
    onChange?.(finalClients);
  };

  const newClient = useMemo(() => {
    if (
      allowCreate &&
      currentInputValue.trim() &&
      !clients.some((client) => matchesCompanyName(client, currentInputValue))
    ) {
      return { value: currentInputValue.trim(), name: currentInputValue.trim(), isCreated: true };
    }

    return null;
  }, [currentInputValue, allowCreate, clients]);

  const handleNewClientClick = (client) => {
    if (!currentlySubmitting) mutate(client);
  };

  const isClientSelected = useCallback(
    (client) => {
      return selectedClients.find((selectedClient) => selectedClient.uuid === client.uuid);
    },
    [selectedClients]
  );

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  return (
    <ThemeProvider theme={{ isError }}>
      <ClientSelectWrapper ref={wrapperRef} onClick={handleWrapperClick}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        <ClientSelectSelectedClientsContainer>
          {selectedClients.map((client) => (
            <ClientSelectSelectedClientContainer key={client.uuid}>
              <ClientIcon width={16} height={16} fill={'var(--color-data-client-foreground)'} />
              <ClientSelectSelectedClientLabel>{client.name}</ClientSelectSelectedClientLabel>
              <ClientSelectSelectedClientRemoveContainer
                data-action="remove"
                onClick={(e) => handleClientRemoveClick(client, e)}
              >
                <CloseIcon width={9} height={9} fill="var(--color-text)" fillOpacity="1" />
              </ClientSelectSelectedClientRemoveContainer>
            </ClientSelectSelectedClientContainer>
          ))}
          {currentlySubmitting ? (
            <ClientSelectSelectedClientContainer key={'new-client'}>
              <ClientIcon width={16} height={16} fill={'var(--color-data-client-foreground)'} />
              <ClientSelectSelectedClientLabel loading>
                <SpinnerDarkIcon />
              </ClientSelectSelectedClientLabel>
            </ClientSelectSelectedClientContainer>
          ) : null}
          <ClientSelectInput
            ref={inputRef}
            placeholder={selectedClients.length > 0 || currentlySubmitting ? null : placeholder}
            value={currentInputValue}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
          />
        </ClientSelectSelectedClientsContainer>
        <ClientSelectIconContainer>
          <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
        </ClientSelectIconContainer>
        {creationError && <p role="alert">{creationError}</p>}
        <AnimatePresence>
          {isOpen ? (
            <ClientSelectOptionsContainer>
              {newClient ? (
                <ClientSelectOptionContainer
                  key={newClient.value}
                  onClick={() => handleNewClientClick(newClient)}
                >
                  <ClientSelectOptionLabel>{newClient.name}</ClientSelectOptionLabel>
                  <ClientSelectOptionAdd>ADD NEW</ClientSelectOptionAdd>
                </ClientSelectOptionContainer>
              ) : null}
              {list?.map((client) => (
                <ClientSelectOptionContainer
                  key={client.uuid}
                  onClick={() => handleClientClick(client)}
                >
                  <ClientSelectOptionLabel alreadyAdded={isClientSelected(client)}>
                    {client.name}
                  </ClientSelectOptionLabel>
                  {isClientSelected(client) ? (
                    <ClientSelectOptionAlreadyAdded>ALREADY ADDED</ClientSelectOptionAlreadyAdded>
                  ) : null}
                </ClientSelectOptionContainer>
              ))}
            </ClientSelectOptionsContainer>
          ) : null}
        </AnimatePresence>
      </ClientSelectWrapper>
    </ThemeProvider>
  );
}
